import { useState, useCallback } from 'react';

/**
 * useCardBehavior - Hook compartilhado para comportamento de cards
 *
 * Centraliza a lógica comum entre todas as variantes:
 * - Hover state management
 * - Action handler centralizado
 * - Selection click handler
 *
 * TRANSFORMER PATTERN: Lógica compartilhada em um único lugar.
 * Adicionar novo comportamento aqui = todas as variantes recebem automaticamente.
 */
const useCardBehavior = ({
  article,
  selectionMode,
  onSelect,
  onArticleClick,
  onToggleFavorite,
  onToggleRead,
  onEdit,
  onAddTag,
  onAddToCollection,
  onDelete,
  onSendNewsletter,
  onRescrapeSmart
}) => {
  // Hover state (usado em detailed e minimal)
  const [hover, setHover] = useState(false);

  /**
   * Handler centralizado para todas as ações
   * Mapeia actionType para a função handler apropriada
   */
  const handleAction = useCallback((actionType, article) => {
    switch (actionType) {
      case 'addTag':
        onAddTag && onAddTag(article);
        break;
      case 'addCollection':
        onAddToCollection && onAddToCollection(article);
        break;
      case 'edit':
        onEdit && onEdit(article);
        break;
      case 'delete':
        onDelete && onDelete(article._id);
        break;
      case 'toggleFavorite':
        onToggleFavorite && onToggleFavorite(article._id);
        break;
      case 'toggleRead':
        onToggleRead && onToggleRead(article._id);
        break;
      case 'updateSentiment':
        // Tratado diretamente pelo ActionButtons via onUpdateSentiment
        break;
      case 'sendNewsletter':
        onSendNewsletter && onSendNewsletter(article);
        break;
      case 'rescrapeSmart':
        onRescrapeSmart && onRescrapeSmart(article);
        break;
      default:
        console.warn(`Ação não reconhecida: ${actionType}`);
        break;
    }
  }, [onAddTag, onAddToCollection, onEdit, onDelete, onToggleFavorite, onToggleRead, onSendNewsletter]);

  /**
   * Handler de clique no card
   * Gerencia modo de seleção vs. visualização normal
   */
  const handleCardClick = useCallback((e) => {
    // Modo seleção: toggle selection
    if (selectionMode && onSelect) {
      e.stopPropagation();
      onSelect(article._id);
      return;
    }

    // Modo normal: abrir modal/detalhe
    if (onArticleClick) {
      onArticleClick(article);
    }
  }, [selectionMode, onSelect, article, onArticleClick]);

  /**
   * Handlers para hover (otimizados com useCallback)
   */
  const handleMouseEnter = useCallback(() => setHover(true), []);
  const handleMouseLeave = useCallback(() => setHover(false), []);

  return {
    // State
    hover,

    // Handlers
    handleAction,
    handleCardClick,
    handleMouseEnter,
    handleMouseLeave
  };
};

export default useCardBehavior;
